/**
 * Enhanced Error Handling System
 * Comprehensive error handling with specific error types, retry mechanisms, and graceful degradation
 */

// Error Types and Classifications
export const ERROR_TYPES = {
  // Network and Connectivity Errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  CONNECTIVITY_ERROR: 'CONNECTIVITY_ERROR',
  
  // Authentication and Authorization Errors
  AUTH_ERROR: 'AUTH_ERROR',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  
  // Business Logic Errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BUSINESS_RULE_ERROR: 'BUSINESS_RULE_ERROR',
  DUPLICATE_OPERATION: 'DUPLICATE_OPERATION',
  
  // Server Errors
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  RATE_LIMIT_ERROR: 'RATE_LIMIT_ERROR',
  
  // Data Errors
  DATA_NOT_FOUND: 'DATA_NOT_FOUND',
  DATA_CORRUPTION: 'DATA_CORRUPTION',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  
  // Assignment Specific Errors
  ASSIGNMENT_CONFLICT: 'ASSIGNMENT_CONFLICT',
  CLAIM_ALREADY_ASSIGNED: 'CLAIM_ALREADY_ASSIGNED',
  CLAIM_NOT_ASSIGNABLE: 'CLAIM_NOT_ASSIGNABLE',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS'
};

// Error Severity Levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium', 
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Retry Configuration
export const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  exponentialBackoff: true,
  retryableErrors: [
    ERROR_TYPES.NETWORK_ERROR,
    ERROR_TYPES.TIMEOUT_ERROR,
    ERROR_TYPES.SERVICE_UNAVAILABLE,
    ERROR_TYPES.RATE_LIMIT_ERROR
  ]
};

/**
 * Enhanced Error Class with detailed context and recovery suggestions
 */
export class AssignmentError extends Error {
  constructor(type, message, context = {}) {
    super(message);
    this.name = 'AssignmentError';
    this.type = type;
    this.context = context;
    this.timestamp = new Date().toISOString();
    this.severity = this.determineSeverity();
    this.userMessage = this.generateUserMessage();
    this.recoveryActions = this.generateRecoveryActions();
    this.retryable = this.isRetryable();
  }

  determineSeverity() {
    const criticalErrors = [
      ERROR_TYPES.DATA_CORRUPTION,
      ERROR_TYPES.SERVER_ERROR,
      ERROR_TYPES.SERVICE_UNAVAILABLE
    ];
    
    const highErrors = [
      ERROR_TYPES.AUTH_ERROR,
      ERROR_TYPES.TOKEN_EXPIRED,
      ERROR_TYPES.PERMISSION_DENIED
    ];
    
    const mediumErrors = [
      ERROR_TYPES.BUSINESS_RULE_ERROR,
      ERROR_TYPES.ASSIGNMENT_CONFLICT,
      ERROR_TYPES.CLAIM_ALREADY_ASSIGNED
    ];
    
    if (criticalErrors.includes(this.type)) return ERROR_SEVERITY.CRITICAL;
    if (highErrors.includes(this.type)) return ERROR_SEVERITY.HIGH;
    if (mediumErrors.includes(this.type)) return ERROR_SEVERITY.MEDIUM;
    return ERROR_SEVERITY.LOW;
  }

  generateUserMessage() {
    const messages = {
      [ERROR_TYPES.NETWORK_ERROR]: 'Connection lost. Please check your internet connection.',
      [ERROR_TYPES.TIMEOUT_ERROR]: 'Request timed out. The server is taking longer than expected.',
      [ERROR_TYPES.AUTH_ERROR]: 'Authentication failed. Please log in again.',
      [ERROR_TYPES.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
      [ERROR_TYPES.PERMISSION_DENIED]: 'You don\'t have permission to perform this action.',
      [ERROR_TYPES.VALIDATION_ERROR]: 'Please check the information provided and try again.',
      [ERROR_TYPES.BUSINESS_RULE_ERROR]: 'This action cannot be completed due to business rules.',
      [ERROR_TYPES.SERVER_ERROR]: 'Server error occurred. Our team has been notified.',
      [ERROR_TYPES.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable. Please try again shortly.',
      [ERROR_TYPES.DATA_NOT_FOUND]: 'The requested information could not be found.',
      [ERROR_TYPES.ASSIGNMENT_CONFLICT]: 'This claim has been assigned to another officer.',
      [ERROR_TYPES.CLAIM_ALREADY_ASSIGNED]: 'This claim is already assigned to you.',
      [ERROR_TYPES.CLAIM_NOT_ASSIGNABLE]: 'This claim cannot be assigned at this time.',
      [ERROR_TYPES.INSUFFICIENT_PERMISSIONS]: 'You don\'t have the required permissions for this action.'
    };
    
    return messages[this.type] || 'An unexpected error occurred. Please try again.';
  }

  generateRecoveryActions() {
    const actions = {
      [ERROR_TYPES.NETWORK_ERROR]: [
        { action: 'retry', label: 'Retry', delay: 2000 },
        { action: 'refresh', label: 'Refresh Page' }
      ],
      [ERROR_TYPES.TIMEOUT_ERROR]: [
        { action: 'retry', label: 'Try Again', delay: 3000 },
        { action: 'refresh', label: 'Refresh Page' }
      ],
      [ERROR_TYPES.AUTH_ERROR]: [
        { action: 'login', label: 'Log In Again' }
      ],
      [ERROR_TYPES.TOKEN_EXPIRED]: [
        { action: 'login', label: 'Log In Again' }
      ],
      [ERROR_TYPES.PERMISSION_DENIED]: [
        { action: 'contact_admin', label: 'Contact Administrator' }
      ],
      [ERROR_TYPES.VALIDATION_ERROR]: [
        { action: 'retry', label: 'Try Again' }
      ],
      [ERROR_TYPES.SERVER_ERROR]: [
        { action: 'retry', label: 'Retry', delay: 5000 },
        { action: 'contact_support', label: 'Contact Support' }
      ],
      [ERROR_TYPES.SERVICE_UNAVAILABLE]: [
        { action: 'retry', label: 'Retry', delay: 10000 }
      ],
      [ERROR_TYPES.ASSIGNMENT_CONFLICT]: [
        { action: 'refresh', label: 'Refresh Claims List' },
        { action: 'view_claim', label: 'View Claim Details' }
      ],
      [ERROR_TYPES.CLAIM_ALREADY_ASSIGNED]: [
        { action: 'refresh', label: 'Refresh Claims List' }
      ]
    };
    
    return actions[this.type] || [
      { action: 'retry', label: 'Try Again' },
      { action: 'refresh', label: 'Refresh Page' }
    ];
  }

  isRetryable() {
    return RETRY_CONFIG.retryableErrors.includes(this.type);
  }

  toAnalyticsEvent() {
    return {
      event: 'assignment_error',
      error_type: this.type,
      error_severity: this.severity,
      error_message: this.message,
      user_message: this.userMessage,
      retryable: this.retryable,
      context: this.context,
      timestamp: this.timestamp
    };
  }
}

/**
 * Error Classification Helper
 * Analyzes raw errors and classifies them into specific types
 */
export class ErrorClassifier {
  static classifyError(error, context = {}) {
    // Network and connectivity errors
    if (error.name === 'AbortError') {
      return new AssignmentError(ERROR_TYPES.TIMEOUT_ERROR, 'Request was cancelled', context);
    }
    
    if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
      return new AssignmentError(ERROR_TYPES.NETWORK_ERROR, 'Network connection failed', context);
    }
    
    if (error.code === 'ENOTFOUND' || error.message?.includes('getaddrinfo ENOTFOUND')) {
      return new AssignmentError(ERROR_TYPES.CONNECTIVITY_ERROR, 'Cannot connect to server', context);
    }
    
    // HTTP Response errors
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 401:
          return new AssignmentError(
            ERROR_TYPES.AUTH_ERROR,
            data?.message || 'Authentication failed',
            { ...context, status }
          );
          
        case 403:
          return new AssignmentError(
            ERROR_TYPES.PERMISSION_DENIED,
            data?.message || 'Access denied',
            { ...context, status }
          );
          
        case 404:
          return new AssignmentError(
            ERROR_TYPES.DATA_NOT_FOUND,
            data?.message || 'Resource not found',
            { ...context, status }
          );
          
        case 409:
          // Assignment conflicts
          if (data?.code === 'CLAIM_ALREADY_ASSIGNED') {
            return new AssignmentError(
              ERROR_TYPES.CLAIM_ALREADY_ASSIGNED,
              data?.message || 'Claim is already assigned',
              { ...context, status, conflictData: data }
            );
          }
          
          if (data?.code === 'ASSIGNMENT_CONFLICT') {
            return new AssignmentError(
              ERROR_TYPES.ASSIGNMENT_CONFLICT,
              data?.message || 'Assignment conflict occurred',
              { ...context, status, conflictData: data }
            );
          }
          
          return new AssignmentError(
            ERROR_TYPES.BUSINESS_RULE_ERROR,
            data?.message || 'Business rule violation',
            { ...context, status }
          );
          
        case 422:
          return new AssignmentError(
            ERROR_TYPES.VALIDATION_ERROR,
            data?.message || 'Validation failed',
            { ...context, status, validationErrors: data?.errors }
          );
          
        case 429:
          return new AssignmentError(
            ERROR_TYPES.RATE_LIMIT_ERROR,
            data?.message || 'Too many requests',
            { ...context, status, retryAfter: data?.retryAfter }
          );
          
        case 500:
        case 502:
        case 503:
          return new AssignmentError(
            status === 503 ? ERROR_TYPES.SERVICE_UNAVAILABLE : ERROR_TYPES.SERVER_ERROR,
            data?.message || 'Server error occurred',
            { ...context, status }
          );
          
        default:
          return new AssignmentError(
            ERROR_TYPES.SERVER_ERROR,
            data?.message || `HTTP ${status} error`,
            { ...context, status }
          );
      }
    }
    
    // Assignment-specific business logic errors
    if (error.message?.includes('Claims_Officer')) {
      return new AssignmentError(
        ERROR_TYPES.INSUFFICIENT_PERMISSIONS,
        'Only Claims Officers can perform this action',
        context
      );
    }
    
    if (error.message?.includes('Claim ID is required')) {
      return new AssignmentError(
        ERROR_TYPES.VALIDATION_ERROR,
        'Invalid claim identifier',
        context
      );
    }
    
    // Token expiration
    if (error.message?.includes('token') && error.message?.includes('expired')) {
      return new AssignmentError(
        ERROR_TYPES.TOKEN_EXPIRED,
        'Your session has expired',
        context
      );
    }
    
    // Default classification
    return new AssignmentError(
      ERROR_TYPES.SERVER_ERROR,
      error.message || 'An unexpected error occurred',
      { ...context, originalError: error.name }
    );
  }
}

/**
 * Retry Mechanism with Exponential Backoff
 */
export class RetryManager {
  static async executeWithRetry(operation, context = {}, customConfig = {}) {
    const config = { ...RETRY_CONFIG, ...customConfig };
    let lastError;
    
    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        const result = await operation();
        
        // Log successful retry if not first attempt
        if (attempt > 0) {
          console.log(`✅ [RetryManager] Operation succeeded on attempt ${attempt + 1}`, {
            context,
            totalAttempts: attempt + 1
          });
        }
        
        return result;
      } catch (error) {
        lastError = ErrorClassifier.classifyError(error, {
          ...context,
          attempt: attempt + 1,
          maxAttempts: config.maxRetries + 1
        });
        
        // Don't retry if not retryable or on last attempt
        if (!lastError.retryable || attempt === config.maxRetries) {
          console.error(`❌ [RetryManager] Operation failed after ${attempt + 1} attempts`, {
            context,
            error: lastError,
            finalAttempt: true
          });
          throw lastError;
        }
        
        // Calculate delay with exponential backoff
        const delay = config.exponentialBackoff
          ? Math.min(config.baseDelay * Math.pow(2, attempt), config.maxDelay)
          : config.baseDelay;
        
        console.warn(`⚠️ [RetryManager] Attempt ${attempt + 1} failed, retrying in ${delay}ms`, {
          context,
          error: lastError.type,
          nextDelay: delay
        });
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }
}

/**
 * Circuit Breaker Pattern for Service Reliability
 */
export class CircuitBreaker {
  constructor(serviceName, config = {}) {
    this.serviceName = serviceName;
    this.config = {
      failureThreshold: 5,
      resetTimeout: 60000, // 1 minute
      monitoringPeriod: 300000, // 5 minutes
      ...config
    };
    this.reset();
  }

  reset() {
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
  }

  async execute(operation, context = {}) {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime >= this.config.resetTimeout) {
        this.state = 'HALF_OPEN';
        this.successCount = 0;
        console.log(`🔄 [CircuitBreaker:${this.serviceName}] Moving to HALF_OPEN state`);
      } else {
        throw new AssignmentError(
          ERROR_TYPES.SERVICE_UNAVAILABLE,
          `${this.serviceName} service is temporarily unavailable`,
          { ...context, circuitBreakerState: this.state }
        );
      }
    }

    try {
      const result = await operation();
      
      if (this.state === 'HALF_OPEN') {
        this.successCount++;
        if (this.successCount >= 3) {
          this.reset();
          console.log(`✅ [CircuitBreaker:${this.serviceName}] Service recovered, circuit closed`);
        }
      }
      
      return result;
    } catch (error) {
      this.failureCount++;
      this.lastFailureTime = Date.now();
      
      if (this.failureCount >= this.config.failureThreshold) {
        this.state = 'OPEN';
        console.error(`🔴 [CircuitBreaker:${this.serviceName}] Circuit opened due to repeated failures`, {
          failureCount: this.failureCount,
          threshold: this.config.failureThreshold
        });
      }
      
      throw ErrorClassifier.classifyError(error, {
        ...context,
        circuitBreakerState: this.state,
        failureCount: this.failureCount
      });
    }
  }
}

/**
 * Graceful Degradation Helper
 */
export class GracefulDegradation {
  static async executeWithFallback(primaryOperation, fallbackOperation, context = {}) {
    try {
      return await primaryOperation();
    } catch (primaryError) {
      console.warn(`⚠️ [GracefulDegradation] Primary operation failed, using fallback`, {
        context,
        primaryError: primaryError.message
      });
      
      try {
        const fallbackResult = await fallbackOperation();
        return {
          ...fallbackResult,
          _degraded: true,
          _primaryError: primaryError.message
        };
      } catch (fallbackError) {
        // Both primary and fallback failed
        console.error(`❌ [GracefulDegradation] Both primary and fallback operations failed`, {
          context,
          primaryError: primaryError.message,
          fallbackError: fallbackError.message
        });
        throw ErrorClassifier.classifyError(primaryError, context);
      }
    }
  }
}

export default {
  ERROR_TYPES,
  ERROR_SEVERITY,
  RETRY_CONFIG,
  AssignmentError,
  ErrorClassifier,
  RetryManager,
  CircuitBreaker,
  GracefulDegradation
};